import { useEffect, useState } from 'react'
import { Shield, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/stores/auth-store'
import { MfaEnrollDialog } from '@/components/settings/MfaEnrollDialog'

const DISMISS_KEY = 'bc.mfa.dismissedUntil'
const DISMISS_DAYS = 7

/**
 * Soft prompt banner for users without MFA who are not in the mfaRequired
 * cohort. Dismissible per-browser for 7 days via localStorage.
 */
export function MfaPromptBanner() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const mfaEnrolled = useAuthStore((s) => s.mfaEnrolled)
  const mfaRequired = useAuthStore((s) => s.mfaRequired)
  const [dismissed, setDismissed] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const raw = window.localStorage.getItem(DISMISS_KEY)
    if (!raw) {
      setDismissed(false)
      return
    }
    const until = Number(raw)
    if (Number.isFinite(until) && until > Date.now()) {
      setDismissed(true)
    } else {
      window.localStorage.removeItem(DISMISS_KEY)
      setDismissed(false)
    }
  }, [])

  if (!isAuthenticated || mfaEnrolled || mfaRequired || dismissed) {
    return null
  }

  const handleDismiss = () => {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000
    window.localStorage.setItem(DISMISS_KEY, String(until))
    setDismissed(true)
  }

  return (
    <>
      <div className="flex items-start gap-3 rounded-lg p-3 mb-4 bg-[hsl(var(--ai-accent-soft))] border border-[hsl(var(--ai-accent))]/20">
        <Shield className="h-4 w-4 mt-0.5 text-[hsl(var(--ai-accent))]" />
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            Add two-factor authentication to keep your bids secure. It takes about a minute.
          </p>
          <div className="flex gap-2 mt-2">
            <Button size="sm" type="button" onClick={() => setDialogOpen(true)}>
              Set up now
            </Button>
            <Button size="sm" type="button" variant="ghost" onClick={handleDismiss}>
              Remind me later
            </Button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <MfaEnrollDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </>
  )
}
